import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Phone, Mail, ChevronDown } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';


export type PageType =
  | 'home'
  | 'about'
  | 'infrastructure'
  | 'products'
  | 'contact'
  | 'news'
  | 'steel-plant'
  | 'cement-plant'
  | 'mineral-handling'
  | 'defence'
  | 'renewable-energy'
  | 'boiler-components';

type HeaderProps = {
  currentPage: PageType;
  onNavigate: (page: PageType) => void;
};

const industriesDropdown: { label: string; page: PageType }[] = [
  { label: 'All Industries', page: 'products' },
  { label: 'Renewable Energy', page: 'renewable-energy' },
  { label: 'Steel Plant', page: 'steel-plant' },
  { label: 'Cement Plant', page: 'cement-plant' },
  { label: 'Mining and Mineral Handling', page: 'mineral-handling' },
  { label: 'Defence', page: 'defence' },
  { label: 'Boiler Components', page: 'boiler-components' },
];

export const Header: React.FC<HeaderProps> = ({ currentPage, onNavigate }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isDark } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  const isIndustriesPage = (page: PageType) => {
    return ['products', 'steel-plant', 'cement-plant', 'mineral-handling', 'defence', 'renewable-energy', 'boiler-components'].includes(page);
  };

  const [industriesOpen, setIndustriesOpen] = useState(false);
  const [mobileIndustriesOpen, setMobileIndustriesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIndustriesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavClick = (page: PageType) => {
    onNavigate(page);
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  return (
    <>
      {/* Top Bar - always white with blue text */}
      <div className="hidden md:block w-full py-2 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-6 text-sm">
            <a href="tel:+914312500547" className="flex items-center gap-2 text-vmil-blue hover:text-vmil-blue/80 transition-colors">
              <Phone className="w-4 h-4" />
              <span>+91 4312500547</span>
            </a>
            <a href="mailto:contactus@vmil.in" className="flex items-center gap-2 text-vmil-blue hover:text-vmil-blue/80 transition-colors">
              <Mail className="w-4 h-4" />
              <span>contactus@vmil.in</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled
        ? isDark
          ? 'bg-vmil-blue/95 backdrop-blur-md shadow-lg'
          : 'bg-white/95 backdrop-blur-md shadow-lg'
        : isDark
          ? 'bg-vmil-blue/90'
          : 'bg-white'
        }`}>
        <div className="container mx-auto px-4">
          <div className="flex h-20 items-center justify-between">
            {/* Logo */}
            <button
              onClick={() => handleNavClick('home')}
              className="flex items-center gap-2 cursor-pointer"
            >
              <img
                src="/vmil-logo-transparent.png"
                alt="Velmurugan Industries"
                className="h-10 sm:h-12 md:h-16 w-auto object-contain"
              />
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {/* Home */}
              <button
                onClick={() => handleNavClick('home')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${currentPage === 'home'
                  ? isDark ? 'bg-vmil-blue text-white' : 'bg-vmil-blue text-white'
                  : isDark
                    ? 'text-white hover:text-vmil-blue hover:bg-white/10'
                    : 'text-vmil-steel hover:text-vmil-blue hover:bg-vmil-blue/5'
                  }`}
              >
                Home
              </button>

              {/* About Us */}
              <button
                onClick={() => handleNavClick('about')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${currentPage === 'about'
                  ? isDark ? 'bg-vmil-blue text-white' : 'bg-vmil-blue text-white'
                  : isDark
                    ? 'text-white hover:text-vmil-blue hover:bg-white/10'
                    : 'text-vmil-steel hover:text-vmil-blue hover:bg-vmil-blue/5'
                  }`}
              >
                About Us
              </button>

              {/* Infrastructure */}
              <button
                onClick={() => handleNavClick('infrastructure')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${currentPage === 'infrastructure'
                  ? isDark ? 'bg-vmil-blue text-white' : 'bg-vmil-blue text-white'
                  : isDark
                    ? 'text-white hover:text-vmil-blue hover:bg-white/10'
                    : 'text-vmil-steel hover:text-vmil-blue hover:bg-vmil-blue/5'
                  }`}
              >
                Infrastructure
              </button>

              {/* Industries - Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIndustriesOpen(!industriesOpen)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center gap-1 ${isIndustriesPage(currentPage)
                    ? isDark ? 'bg-vmil-blue text-white' : 'bg-vmil-blue text-white'
                    : isDark
                      ? 'text-white hover:text-vmil-blue hover:bg-white/10'
                      : 'text-vmil-steel hover:text-vmil-blue hover:bg-vmil-blue/5'
                    }`}
                >
                  Industries
                  <ChevronDown className={`w-4 h-4 transition-transform ${industriesOpen ? 'rotate-180' : ''}`} />
                </button>
                {industriesOpen && (
                  <div className={`absolute top-full left-0 mt-1 min-w-[220px] rounded-lg shadow-xl border py-2 z-50 ${isDark ? 'bg-vmil-navy border-white/10' : 'bg-white border-gray-200'
                    }`}>
                    {industriesDropdown.map((item) => (
                      <button
                        key={item.page}
                        onClick={() => {
                          handleNavClick(item.page);
                          setIndustriesOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left text-sm font-medium transition-colors ${currentPage === item.page
                          ? isDark ? 'bg-vmil-blue/20 text-vmil-blue' : 'bg-vmil-blue/10 text-vmil-blue'
                          : isDark
                            ? 'text-white hover:bg-white/10'
                            : 'text-vmil-steel hover:bg-vmil-blue/5'
                          }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* News */}
              <button
                onClick={() => handleNavClick('news')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${currentPage === 'news'
                  ? isDark ? 'bg-vmil-blue text-white' : 'bg-vmil-blue text-white'
                  : isDark
                    ? 'text-white hover:text-vmil-blue hover:bg-white/10'
                    : 'text-vmil-steel hover:text-vmil-blue hover:bg-vmil-blue/5'
                  }`}
              >
                News
              </button>

              {/* Contact */}
              <button
                onClick={() => handleNavClick('contact')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${currentPage === 'contact'
                  ? isDark ? 'bg-vmil-blue text-white' : 'bg-vmil-blue text-white'
                  : isDark
                    ? 'text-white hover:text-vmil-blue hover:bg-white/10'
                    : 'text-vmil-steel hover:text-vmil-blue hover:bg-vmil-blue/5'
                  }`}
              >
                Contact
              </button>


            </nav>

            {/* Mobile Menu Button */}
            <div className="flex items-center gap-3 md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`p-2 rounded-lg ${isDark ? 'text-white hover:bg-white/10' : 'text-vmil-blue hover:bg-vmil-blue/5'
                  }`}
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className={`md:hidden border-t ${isDark ? 'bg-vmil-blue/95 border-white/10' : 'bg-white border-gray-200'
            }`}>
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
              {/* Home */}
              <button
                onClick={() => handleNavClick('home')}
                className={`px-4 py-3 rounded-lg text-left font-semibold transition-all ${currentPage === 'home'
                  ? isDark ? 'bg-vmil-blue text-white' : 'bg-vmil-blue text-white'
                  : isDark
                    ? 'text-white hover:bg-white/10'
                    : 'text-vmil-steel hover:bg-vmil-blue/5'
                  }`}
              >
                Home
              </button>

              {/* About Us */}
              <button
                onClick={() => handleNavClick('about')}
                className={`px-4 py-3 rounded-lg text-left font-semibold transition-all ${currentPage === 'about'
                  ? isDark ? 'bg-vmil-blue text-white' : 'bg-vmil-blue text-white'
                  : isDark
                    ? 'text-white hover:bg-white/10'
                    : 'text-vmil-steel hover:bg-vmil-blue/5'
                  }`}
              >
                About Us
              </button>

              {/* Infrastructure */}
              <button
                onClick={() => handleNavClick('infrastructure')}
                className={`px-4 py-3 rounded-lg text-left font-semibold transition-all ${currentPage === 'infrastructure'
                  ? isDark ? 'bg-vmil-blue text-white' : 'bg-vmil-blue text-white'
                  : isDark
                    ? 'text-white hover:bg-white/10'
                    : 'text-vmil-steel hover:bg-vmil-blue/5'
                  }`}
              >
                Infrastructure
              </button>

              {/* Industries - Collapsible */}
              <div className="space-y-1">
                <button
                  onClick={() => setMobileIndustriesOpen(!mobileIndustriesOpen)}
                  className={`w-full px-4 py-3 rounded-lg text-left font-semibold transition-all flex items-center justify-between ${isIndustriesPage(currentPage)
                      ? 'bg-vmil-blue text-white'
                      : isDark
                        ? 'text-white hover:bg-white/10'
                        : 'text-vmil-steel hover:bg-vmil-blue/5'
                    }`}
                >
                  Industries
                  <ChevronDown className={`w-4 h-4 transition-transform ${mobileIndustriesOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileIndustriesOpen && (
                  <div className="pl-2">
                    {industriesDropdown.map((item) => (
                      <button
                        key={item.page}
                        onClick={() => handleNavClick(item.page)}
                        className={`w-full px-6 py-2.5 rounded-lg text-left text-sm font-medium transition-all ${currentPage === item.page
                          ? isDark ? 'bg-vmil-blue text-white' : 'bg-vmil-blue/10 text-vmil-blue'
                          : isDark
                            ? 'text-white hover:bg-white/10'
                            : 'text-vmil-steel hover:bg-vmil-blue/5'
                          }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* News */}
              <button
                onClick={() => handleNavClick('news')}
                className={`px-4 py-3 rounded-lg text-left font-semibold transition-all ${currentPage === 'news'
                  ? isDark ? 'bg-vmil-blue text-white' : 'bg-vmil-blue text-white'
                  : isDark
                    ? 'text-white hover:bg-white/10'
                    : 'text-vmil-steel hover:bg-vmil-blue/5'
                  }`}
              >
                News
              </button>

              {/* Contact */}
              <button
                onClick={() => handleNavClick('contact')}
                className={`px-4 py-3 rounded-lg text-left font-semibold transition-all ${currentPage === 'contact'
                  ? isDark ? 'bg-vmil-blue text-white' : 'bg-vmil-blue text-white'
                  : isDark
                    ? 'text-white hover:bg-white/10'
                    : 'text-vmil-steel hover:bg-vmil-blue/5'
                  }`}
              >
                Contact
              </button>

              {/* Mobile Contact Info */}
              <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <a href="tel:+914312500547" className={`flex items-center gap-2 py-2 ${isDark ? 'text-white' : 'text-vmil-steel'
                  }`}>
                  <Phone className="w-4 h-4" />
                  <span>+91 4312500547</span>
                </a>
                <a href="mailto:contactus@vmil.in" className={`flex items-center gap-2 py-2 ${isDark ? 'text-white' : 'text-vmil-steel'
                  }`}>
                  <Mail className="w-4 h-4" />
                  <span>contactus@vmil.in</span>
                </a>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};
